import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { LogOut, History as HistoryIcon, ShieldCheck, LogIn } from "lucide-react";
import { useGeo } from "@/lib/geo";
import ThemeToggle from "@/components/apexture/ThemeToggle";
import SupportChatWidget from "./SupportChatWidget";

export default function Layout({ children }) {
  const { user, logout, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { isIndia } = useGeo();
  const onLanding = location.pathname === "/";

  const tierBadge = () => {
    if (!user) return null;
    if (user.role === "admin") return <Badge className="bg-honey text-forest border-0">Admin</Badge>;
    if (user.has_access) return <Badge className="bg-sage text-white border-0">Active</Badge>;
    return <Badge className="bg-bone text-forest border border-forest/20">Trial</Badge>;
  };

  return (
    <div className="min-h-screen bg-bone text-ink flex flex-col dark:bg-background dark:text-foreground">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-bone/85 border-b border-forest/10 dark:bg-background/80 dark:border-sage/20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 group shrink-0" data-testid="brand-logo">
            <img
              src="/logo192.png"
              alt="Apexture logo"
              width="36"
              height="36"
              className="w-9 h-9 object-contain"
            />
            <span className="font-heading text-xl font-semibold tracking-tight text-forest dark:text-sage">Apexture</span>
          </Link>

          <nav className="hidden md:flex items-center gap-7 font-medium text-sm">
            <Link to="/safety-screen" className="hover:text-forest transition-colors" data-testid="nav-safety">Assessment</Link>
            <Link to="/pricing" className="hover:text-forest transition-colors" data-testid="nav-pricing">Pricing</Link>
            {user && (
              <Link to="/history" className="hover:text-forest transition-colors flex items-center gap-1" data-testid="nav-history">
                <HistoryIcon className="w-4 h-4" /> History
              </Link>
            )}
            {user?.role === "admin" && (
              <Link to="/admin" className="hover:text-forest transition-colors flex items-center gap-1" data-testid="nav-admin">
                <ShieldCheck className="w-4 h-4" /> Admin
              </Link>
            )}
            <Link to="/feedback" className="hover:text-forest transition-colors" data-testid="nav-feedback">Feedback</Link>
          </nav>

          <div className="flex items-center gap-3 shrink-0">
            <ThemeToggle />
            {loading ? null : user ? (
              <>
                <div className="hidden sm:flex items-center gap-2">
                  {tierBadge()}
                  <span className="text-sm font-medium" data-testid="user-name">{user.name?.split(" ")[0]}</span>
                </div>
                <Button variant="ghost" size="sm" onClick={logout} data-testid="logout-btn">
                  <LogOut className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <>
                <Button variant="ghost" size="sm" onClick={() => navigate("/login")} data-testid="login-nav-btn">
                  <LogIn className="w-4 h-4 mr-1" /> Sign in
                </Button>
                <Button
                  onClick={() => navigate("/signup")}
                  className="bg-forest hover:bg-forest/90 text-bone rounded-full px-5"
                  data-testid="signup-nav-btn"
                >
                  Sign up
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      <main className={onLanding ? "" : "max-w-7xl w-full mx-auto px-6 lg:px-10 py-10"}>
        {children}
      </main>

      <footer className="mt-auto border-t border-forest/10 py-8 bg-white/50 dark:bg-card/40 dark:border-sage/15">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4 gap-6 text-sm">
          <div>
            <p className="font-heading text-forest font-semibold mb-2">Apexture</p>
            <p className="text-xs text-muted-foreground leading-relaxed">Ergonomic & posture-awareness platform. Not a substitute for medical care.</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Product</p>
            <ul className="space-y-1">
              <li><Link to="/safety-screen" className="hover:text-forest">Assessment</Link></li>
              <li><Link to="/pricing" className="hover:text-forest">Pricing</Link></li>
              <li><Link to="/feedback" className="hover:text-forest">Feedback</Link></li>
            </ul>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Legal</p>
            <ul className="space-y-1">
              <li><Link to="/terms" className="hover:text-forest">Terms of Service</Link></li>
              <li><Link to="/privacy" className="hover:text-forest">Privacy Policy</Link></li>
              {isIndia && <li><Link to="/refund" className="hover:text-forest">Refund Policy</Link></li>}
              <li><Link to="/disclaimer" className="hover:text-forest">Medical Disclaimer</Link></li>
            </ul>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Contact</p>
            <ul className="space-y-1 text-xs">
              <li>Grievance Officer</li>
              <li><a href="mailto:path@truepathcareer.com" className="text-forest hover:underline break-all">path@truepathcareer.com</a></li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-6 pt-6 border-t border-forest/10 text-center text-xs text-muted-foreground">
          © {new Date().getFullYear()} Apexture. Educational content, not medical advice.
        </div>
      </footer>

      <SupportChatWidget />
    </div>
  );
}
